import React, { useEffect, useState } from "react";
import "./RideReports.css";
import { useNavigate } from "react-router-dom";
import { LogoutButton } from "../Common/LogoutButton";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { apiFetch } from "../../utils/api";
import Loader from "../Common/Loader";

// const BASE_URL = process.env.REACT_APP_BASE_URL;
const ITEMS_PER_PAGE = 15;

const RideReportsPage = () => {
  const [rideData, setRideData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [filterType, setFilterType] = useState("None");
  const [filterValue, setFilterValue] = useState("");

  // Date filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const navigate = useNavigate();

  const vehicleOptions = ["Forklift", "Crane", "AMR", "Cherry Picker", "Other"];
  const statusOptions = ["pending", "in-progress", "completed"];

  useEffect(() => {
    const fetchRides = async () => {
      try {
        const data = await apiFetch(`/api/reports/AllRides`);
        console.log("ride data = ", data);
        setRideData(data);
        setFilteredData(data);
      } catch (err) {
        console.error("Failed to load ride reports:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRides();
  }, []);

  useEffect(() => {
    let filtered = rideData;

    // Apply basic filters
    if (filterType === "Vehicle Type" && filterValue !== "") {
      filtered = rideData.filter((ride) => ride.vehicleType === filterValue);
    } else if (filterType === "Status" && filterValue !== "") {
      filtered = rideData.filter((ride) => ride.status === filterValue);
    } else if (filterType === "Driver ID" && filterValue.trim() !== "") {
      filtered = rideData.filter((ride) =>
        ride.driverId?.toLowerCase().includes(filterValue.trim().toLowerCase())
      );
    } else if (filterType === "Driver Name" && filterValue.trim() !== "") {
      filtered = rideData.filter((ride) =>
        ride.driverName
          ?.toLowerCase()
          .includes(filterValue.trim().toLowerCase())
      );
    } else if (filterType === "Vehicle Number" && filterValue.trim() !== "") {
      filtered = rideData.filter((ride) =>
        ride.vehicleNumber
          ?.toLowerCase()
          .includes(filterValue.trim().toLowerCase())
      );
    } else if (filterType === "Forklift Number" && filterValue.trim() !== "") {
      filtered = rideData.filter((ride) =>
        ride.forkliftNumber
          ?.toLowerCase()
          .includes(filterValue.trim().toLowerCase())
      );
    } else if (filterType === "Date") {
      // Apply date filter when Date is selected from dropdown
      if (startDate || endDate) {
        filtered = rideData.filter((ride) => {
          const rideDate = ride.createdAt ? new Date(ride.createdAt) : null;
          if (!rideDate) return false;

          const startMatch = !startDate || rideDate >= startDate;
          const endMatch = !endDate || rideDate <= endDate;

          return startMatch && endMatch;
        });
      }
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [filterType, filterValue, rideData, startDate, endDate]);

  // Clear all filters function
  const clearAllFilters = () => {
    setFilterType("None");
    setFilterValue("");
    setStartDate(null);
    setEndDate(null);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filterType !== "None" || filterValue !== "" || startDate || endDate;
  };

  // Helper function for maintenance status badge (similar to ReportsPage)
  const getMaintenanceStatusBadge = (status) => {
    if (!status || status === "N/A")
      return <span className="status-badge">N/A</span>;

    switch (status.toLowerCase()) {
      case "pending":
        return <span className="status-badge pending">Pending</span>;
      case "accepted":
        return <span className="status-badge accepted">Accepted</span>;
      case "completed":
        return <span className="status-badge completed">Completed</span>;
      case "rejected":
        return <span className="status-badge rejected">Rejected</span>;
      default:
        return <span className="status-badge">N/A</span>;
    }
  };

  // Helper function for vehicle type badge (similar to ReportsPage)
  const getVehicleTypeBadge = (type) => {
    if (!type || type === "N/A")
      return <span className="vehicle-badge">N/A</span>;

    switch (type.toLowerCase()) {
      case "forklift":
        return <span className="vehicle-badge forklift">Forklift</span>;
      case "crane":
        return <span className="vehicle-badge crane">Crane</span>;
      case "cherry picker":
        return (
          <span className="vehicle-badge cherry-picker">Cherry Picker</span>
        );
      case "amr":
        return <span className="vehicle-badge amr">AMR</span>;
      default:
        return <span className="vehicle-badge">{type}</span>;
    }
  };

  // CSV Download function
  const downloadCSV = () => {
    if (!filteredData.length) return;

    const headers = [
      "Ride Id",
      "Requester Name",
      "From Location",
      "To Location",
      "Vehicle Type",
      "Vehicle Number",
      "Forklift Number",
      "Status",
      "Created At",
      "Driver ID",
      "Driver Name",
      "Start Time",
      "End Time",
      "Material Pick Time",
      "Material Drop Time",
      "Gap (Pick & Drop) (in Mins)",
      "Gap (Pick & Start) (in Mins)",
      "Gap (createdAt & Drop) (in Mins)",
      "Last Maintenance Date",
      "Maintenance Status",
      "Ride Type",
    ];

    const csvData = filteredData.map((ride) => [
      ride.rideId || "",
      ride.requesterName || "",
      ride.fromLocation || "",
      ride.toLocation || "",
      ride.vehicleType || "",
      ride.vehicleNumber || "Unknown",
      ride.forkliftNumber || "Unknown",
      ride.status || "",
      ride.createdAt || "",
      ride.driverId || "",
      ride.driverName || "",
      ride.startTime || "",
      ride.endTime || "",
      ride.materialPickTime || "",
      ride.materialDropTime || "",
      ride.gapPickDrop ? `${ride.gapPickDrop}` : "N/A",
      ride.gapPickStart ? `${ride.gapPickStart}` : "N/A",
      ride.gapCreatedAtDrop ? `${ride.gapCreatedAtDrop}` : "N/A",
      ride.lastMaintenanceDate || "N/A",
      ride.maintenanceStatus || "N/A",
      ride.rideType || "",
    ]);

    // console.log("csv data = ",csvData)

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "ride_reports.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const currentData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading)
    return (
      <p style={{ textAlign: "center" }}>
        {" "}
        <Loader />{" "}
      </p>
    );

  return (
    <div className="reports-container">
      <h1 className="reports-main-heading">Ride Reports</h1>

      <div className="reports-header">
        <button
          onClick={() => navigate("/maintenance-head")}
          className="back-btn"
        >
          ← Back
        </button>
        <LogoutButton />
      </div>

      {/* Download CSV Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          margin: "10px 0",
        }}
      >
        <button onClick={downloadCSV} className="download-btn">
          Download CSV
        </button>
      </div>

      {/* 🔍 Filter controls */}
      <div className="filters">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setFilterValue(""); // ✅ Clear previous filter value
              // Clear date filters when switching away from Date filter
              if (e.target.value !== "Date") {
                setStartDate(null);
                setEndDate(null);
              }
            }}
          >
            <option value="None">Filter By...</option>
            <option value="Vehicle Type">Vehicle Type</option>
            <option value="Vehicle Number">Vehicle Number</option>
            <option value="Forklift Number">Forklift Number</option>
            <option value="Status">Status</option>
            <option value="Driver ID">Driver ID</option>
            <option value="Driver Name">Driver Name</option>
            <option value="Date">Date</option>
          </select>

          {/* Clear All Filters Button */}
          {hasActiveFilters() && (
            <button
              onClick={clearAllFilters}
              style={{
                padding: "8px 16px",
                backgroundColor: "#f56565",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.9rem",
                fontWeight: "500",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#e53e3e")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#f56565")}
            >
              Clear All Filters
            </button>
          )}
        </div>

        {/* Filter-specific dropdowns and inputs */}
        {filterType === "Vehicle Type" && (
          <div style={{ marginTop: "10px" }}>
            <select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            >
              <option value="">All Vehicles</option>
              {vehicleOptions.map((type, idx) => (
                <option key={idx} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        )}

        {filterType === "Status" && (
          <div style={{ marginTop: "10px" }}>
            <select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            >
              <option value="">All Statuses</option>
              {statusOptions.map((status, idx) => (
                <option key={idx} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        )}

        {(filterType === "Driver ID" ||
          filterType === "Driver Name" ||
          filterType === "Vehicle Number" ||
          filterType === "Forklift Number") && (
          <div style={{ marginTop: "10px" }}>
            <input
              type="text"
              placeholder={`Enter ${filterType}`}
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                width: "200px",
                fontSize: "0.9rem",
              }}
            />
          </div>
        )}

        {/* Date Filter */}
        {filterType === "Date" && (
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText="Start Date"
              isClearable
              dateFormat="dd/MM/yyyy"
            />
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText="End Date"
              isClearable
              dateFormat="dd/MM/yyyy"
            />
          </div>
        )}
      </div>

      {/* Filter Summary */}
      {hasActiveFilters() && (
        <div
          style={{
            margin: "10px 0",
            padding: "10px",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px",
            fontSize: "0.9rem",
            color: "#555",
          }}
        >
          <strong>Active Filters:</strong>
          {filterType !== "None" && filterType !== "Date" && filterValue && (
            <span>
              {" "}
              {filterType}: "{filterValue}"
            </span>
          )}
          {filterType === "Date" && (startDate || endDate) && (
            <span>
              {startDate && ` From: ${startDate.toLocaleDateString()}`}
              {endDate && ` To: ${endDate.toLocaleDateString()}`}
            </span>
          )}
          <span>
            {" "}
            | Showing {filteredData.length} of {rideData.length} records
          </span>
        </div>
      )}

      {/* Add scroll hint */}
      <div className="scroll-hint">
        ← Scroll horizontally to see all columns →
      </div>

      <div className="scroll-wrapper">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Ride Id</th>
              <th>Requester Name</th>
              <th>From Location</th>
              <th>To Location</th>
              <th>Vehicle Type</th>
              <th>Vehicle Number</th>
              <th>Forklift Number</th>
              <th>Status</th>
              <th>Created At</th>
              <th>Driver ID</th>
              <th>Driver Name</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Material Pick Time</th>
              <th>Material Drop Time</th>
              <th>Gap (Pick & Drop) (in Mins)</th>
              <th>Gap (Pick & Start) (in Mins)</th>
              <th>Gap (createdAt & Drop) (in Mins) </th>
              <th>Last Maintenance Date</th>
              <th>Maintenance Status</th>
              <th>Ride Type</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((ride, idx) => (
              <tr key={idx}>
                <td>{ride.rideId}</td>
                <td>{ride.requesterName}</td>
                <td>{ride.fromLocation}</td>
                <td>{ride.toLocation}</td>
                <td>{getVehicleTypeBadge(ride.vehicleType)}</td>
                <td>{ride.vehicleNumber || "Unknown"}</td>
                <td>{ride.forkliftNumber || "Unknown"}</td>
                <td>{ride.status}</td>
                <td>{ride.createdAt}</td>
                <td>{ride.driverId}</td>
                <td>{ride.driverName}</td>
                <td>{ride.startTime}</td>
                <td>{ride.endTime}</td>
                <td>{ride.materialPickTime}</td>
                <td>{ride.materialDropTime}</td>
                <td>
                  {ride.gapCreatedAtDrop
                    ? `${Number(ride.gapCreatedAtDrop).toFixed(2)}`
                    : "N/A"}
                </td>
                <td>
                  {ride.gapPickDrop
                    ? `${Number(ride.gapPickDrop).toFixed(2)}`
                    : "N/A"}
                </td>
                <td>
                  {ride.gapPickStart
                    ? `${Number(ride.gapPickStart).toFixed(2)}`
                    : "N/A"}
                </td>

                <td>{ride.lastMaintenanceDate || "N/A"}</td>
                <td>{getMaintenanceStatusBadge(ride.maintenanceStatus)}</td>
                <td>{ride.rideType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination-controls">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          ← Prev
        </button>
        <span className="page-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default RideReportsPage;
